import * as React from 'react';
import { Eye, EyeOff, X, Check, ChevronDown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type Provider = {
  name: string;
  api_base: string;
  models: string[];
  isAggregate?: boolean;
};

type ConfiguredModel = {
  id: string;
  provider: string;
  model: string;
  api_base: string;
  api_key: string;
  isDefault?: boolean;
  secondaryKey?: string;
  secondaryMode?: 'off' | 'auto' | 'custom';
};

// 特色聚合服务商（在底部高亮推荐）
const FEATURED_PROVIDER = 'tianhong';

export function AddModelModal({
  providers,
  onCancel,
  onSave,
}: {
  providers: Record<string, Provider>;
  onCancel: () => void;
  onSave: (m: ConfiguredModel) => void;
}) {
  const [provider, setProvider] = React.useState<string | null>(null);
  const [showProviderList, setShowProviderList] = React.useState(false);
  const [model, setModel] = React.useState('');
  const [baseUrl, setBaseUrl] = React.useState('');
  const [apiKey, setApiKey] = React.useState('');
  const [showKey, setShowKey] = React.useState(false);
  const [isDefault, setIsDefault] = React.useState(false);
  const [error, setError] = React.useState('');

  const providerKeys = React.useMemo(
    () => Object.keys(providers || {}),
    [providers]
  );

  const selectProvider = React.useCallback((key: string) => {
    setProvider(key);
    setShowProviderList(false);
    const p = providers[key];
    if (p) {
      setBaseUrl(p.api_base || '');
      setModel(p.models?.[0] || '');
    }
  }, [providers]);

  const handleSave = React.useCallback(() => {
    if (!provider) { setError('请选择供应商'); return; }
    if (!model.trim()) { setError('请填写模型名称'); return; }
    if (!apiKey.trim()) { setError('请填写 API Key'); return; }
    const selected = providers[provider];
    onSave({
      id: `m-${Date.now()}`,
      provider,
      model: model.trim(),
      api_base: baseUrl.trim() || selected?.api_base || '',
      api_key: apiKey.trim(),
      isDefault,
    });
  }, [provider, model, baseUrl, apiKey, isDefault, onSave, providers]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onCancel}>
      <div className="w-full max-w-lg rounded-xl border bg-background shadow-lg" onClick={(e) => e.stopPropagation()}>
        {/* 头部 */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-lg font-semibold tracking-tight">Add Model</h3>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto p-6">
          {/* 错误提示 */}
          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* ─── Provider 下拉 ─── */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">Provider</label>
            <div className="relative">
              <button
                onClick={() => setShowProviderList((v) => !v)}
                className="flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <span className={provider ? '' : 'text-muted-foreground'}>
                  {provider ? providers[provider]?.name || provider : '— Choose provider —'}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>

              {showProviderList && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border bg-popover shadow-lg">
                  <div className="max-h-64 overflow-y-auto py-1">
                    {providerKeys.map((key) => {
                      const p = providers[key];
                      const featured = key === FEATURED_PROVIDER;
                      return (
                        <button
                          key={key}
                          onClick={() => selectProvider(key)}
                          className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-accent ${
                            featured ? 'bg-primary/5' : ''
                          }`}
                        >
                          <span className="font-medium">{p?.name || key}</span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            {p?.models?.length || 0} models
                            {featured && <Sparkles className="h-3 w-3 text-amber-500" />}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* 特色聚合推荐 */}
                  {providers[FEATURED_PROVIDER] && (
                    <div className="border-t bg-muted/30 px-3 py-2.5">
                      <div className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                        <Sparkles className="h-3 w-3" />
                        {providers[FEATURED_PROVIDER].name}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        一个 Key 通全部前沿模型（claude/gemini/deepseek 等），官方同价，一键切换。
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ─── Model ─── */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">Model</label>
            {provider && providers[provider]?.models?.length > 0 ? (
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                {(providers[provider]?.models || []).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : (
              <Input
                placeholder="例如 deepseek-v4-flash"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              />
            )}
          </div>

          {/* ─── Base URL ─── */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">Base URL</label>
            <Input
              placeholder="https://api.example.com"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </div>

          {/* ─── API Key ─── */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">API Key</label>
            <div className="relative">
              <Input
                type={showKey ? 'text' : 'password'}
                placeholder="sk-..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="pr-10"
              />
              <button
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                title={showKey ? '隐藏 Key' : '显示 Key'}
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">密钥仅保存在本地，不会明文显示</p>
          </div>

          {/* ─── 设为默认 ─── */}
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <button
              onClick={() => setIsDefault((v) => !v)}
              className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                isDefault ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
              }`}
            >
              {isDefault && <Check className="h-3 w-3" />}
            </button>
            设为默认模型
          </label>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave}>
            <Check className="h-4 w-4" />
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}